import { graphql, HeadFC } from 'gatsby';
import DocumentationPage from '@components/DocumentationPage/DocumentationPage';
import Seo from '@components/SEO/SEO';

interface Props {
  data: Queries.DocumentationPageTemplateQuery;
  pageContext: PageContext;
}

const DocumentationPageTemplate = ({ data: { kontentItemPage }, pageContext }: Props) => {
  const body = kontentItemPage?.elements?.content;

  if (!body) {
    return null;
  }

  const ctaMenu = pageContext.product?.elements.ctas?.value as Queries.LinkFragment[];
  const footerContentBody = pageContext.product?.elements.footer_content ?? undefined;
  const menuFooterContentBody = pageContext.product?.elements.menu_footer_content ?? undefined;
  const userFeedback = pageContext.product?.elements?.user_feedback?.value;
  const title = kontentItemPage?.elements?.title?.value || '';

  return (
    <DocumentationPage
      body={body}
      title={title}
      pageContext={pageContext}
      ctaMenu={ctaMenu}
      userFeedback={userFeedback}
      footerContentBody={footerContentBody}
      menuFooterContentBody={menuFooterContentBody}
    />
  );
};

export const Head: HeadFC<Queries.DocumentationPageTemplateQuery, PageContext> = ({
  data: { kontentItemPage, site },
  pageContext,
}) => {
  const isPageHidden =
    kontentItemPage?.elements?.options?.value?.includes('is_page_hidden') ?? false;

  const seo = {
    title: kontentItemPage?.elements?.common_meta_data__title?.value || '',
    description: kontentItemPage?.elements?.common_meta_data__description?.value || '',
    canonicalPageCodename:
      kontentItemPage?.elements?.common_meta_data__canonical?.value?.system?.codename,
    pageContext,
    shouldNotIndex: !!site?.siteMetadata?.isPreview || isPageHidden,
  };
  return <Seo {...seo} />;
};

export const query = graphql`
  query DocumentationPageTemplate($codename: String!, $versionId: String) {
    site {
      siteMetadata {
        isPreview
      }
    }
    kontentItemPage(system: { codename: { eq: $codename } }) {
      elements {
        options {
          value
        }
        common_meta_data__title {
          value
        }
        common_meta_data__description {
          value
        }
        common_meta_data__canonical {
          value {
            ... on kontent_item {
              system {
                codename
              }
            }
          }
        }
        title {
          value
        }
        content {
          images {
            description
            height
            url
            image_id
            width
          }
          links {
            url_slug
            type
            link_id
            codename
          }
          value
          modular_content {
            ... on kontent_item {
              system {
                codename
              }
            }
            ...AnchorHeading
            ...Callout
            ...CodeSnippet
            ...Collapsible
            ...Iframe
            ...Video
            ...Image
            ...SonarScanner
            ...SharedContent
            ...Table
            ...Tabs
            ...Link
          }
        }
        banner {
          value {
            ...Banner
          }
        }
      }
    }
    kontentItemVersion(system: { codename: { eq: $versionId } }) {
      elements {
        banner {
          value {
            ...Banner
          }
        }
      }
    }
  }
`;

export default DocumentationPageTemplate;
